import {
  Outlet,
  createFileRoute,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Skeleton } from "@/components/ui/skeleton";
import { isLoggedIn } from "@/lib/http";
import { isGuest, isMemberAllowedPath } from "@/lib/perms";
import { useLogoutMutation, userInfoQueryOptions } from "@/lib/queries/auth";
import { subscriptionMeQueryOptions } from "@/lib/queries/billing";
import { useOrganizationLocked } from "@/lib/billing-lock";
import { BillingSettings } from "@/routes/_app/-components/BillingSettings";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { SettingsTabsList } from "@/routes/_app/-components/SettingsTabs";

export const Route = createFileRoute("/_app")({
  component: RouteComponent,

  async beforeLoad({
    context: { queryClient },
    location: { searchStr, pathname },
  }) {
    if (pathname === "/pricing") {
      if (isLoggedIn() === true) {
        const userInfo =
          await queryClient.ensureQueryData(userInfoQueryOptions);
        if (isGuest(userInfo) || !userInfo.organization_id) {
          throw redirect({
            to: isGuest(userInfo) ? "/meetings/shared" : "/",
            replace: true,
          });
        }
      }
      return;
    }

    if (isLoggedIn() !== true) {
      throw redirect({
        to: "/auth/login",
        search: {
          redirect: pathname,
          redirectSearch: Object.fromEntries(
            new URLSearchParams(searchStr).entries(),
          ),
        },
      });
    }

    const userInfo = await queryClient.ensureQueryData(userInfoQueryOptions);
    if (isGuest(userInfo) && !isMemberAllowedPath(pathname)) {
      throw redirect({ to: "/meetings/shared", replace: true });
    }
  },
  pendingComponent: () => (
    <div className="flex h-screen w-screen grow items-center justify-center">
      <Skeleton className="h-[calc(100vh-2rem)] w-[calc(100vw-2rem)]" />
    </div>
  ),
});

function RouteComponent() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  if (pathname === "/pricing" && isLoggedIn() !== true) {
    return (
      <div className="h-full overflow-x-hidden overflow-y-auto">
        <Outlet />
      </div>
    );
  }

  return <AuthenticatedApplication />;
}

function AuthenticatedApplication() {
  const user = useSuspenseQuery(userInfoQueryOptions);
  const canUseBilling =
    user.data.user_type !== "GUEST" && Boolean(user.data.organization_id);
  const subscription = useQuery({
    ...subscriptionMeQueryOptions,
    enabled: canUseBilling,
  });
  const organizationLocked = useOrganizationLocked();

  if (canUseBilling && subscription.isPending) {
    return (
      <div className="flex h-screen w-screen grow items-center justify-center">
        <Skeleton className="h-[calc(100vh-2rem)] w-[calc(100vw-2rem)]" />
      </div>
    );
  }

  if (organizationLocked && !user.data.is_superadmin) {
    if (user.data.org_admin) {
      return (
        <main className="min-h-screen bg-background">
          <div className="mx-auto w-full max-w-6xl p-4 md:p-6">
            <Tabs value="billing" className="w-full min-w-0">
              <SettingsTabsList billingOnly />
              <TabsContent value="billing" className="mt-6 space-y-6">
                <BillingSettings />
              </TabsContent>
            </Tabs>
          </div>
        </main>
      );
    }
    return <LockedOrganizationPage />;
  }

  return (
    <DashboardLayout>
      <Outlet />
    </DashboardLayout>
  );
}

function LockedOrganizationPage() {
  const logout = useLogoutMutation();
  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-semibold">Organization access paused</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          This organization&apos;s subscription has lapsed. Ask an organization
          administrator to update the billing details.
        </p>
        <Button
          className="mt-6"
          variant="outline"
          disabled={logout.isPending}
          onClick={() => logout.mutate()}
        >
          Sign out
        </Button>
      </div>
    </main>
  );
}
